import { readMachineStatus } from 'tf-pulse-shared';
import { GetMachinesQuery } from '../../gql/generated';
import { Machine } from '../../types/machines/machine.types';

type MachineRow = GetMachinesQuery['machines']['rows'][number];

/**
 * Flatten a machine from the GraphQL response into the shape used by the
 * machines table, filters and CSV export.
 *
 * Shared so the exported file always matches what the table shows.
 */
export function mapMachineRow(machine: MachineRow): Machine {
  const { status, asOf, isSynthetic, lastRecordedStatus, lastRecordedAt } =
    readMachineStatus(machine.state);

  return {
    // Core identifiers
    id: machine.id,
    machineId: machine.machineId,
    displayName: machine.displayName || machine.machineId || '',

    // Machine properties
    serial: machine.serial || '',
    partNumber: machine.partNumber || '',
    soid: machine.soid || '',
    family: machine.family || '',
    status,
    statusAsOf: asOf ?? null,
    statusIsSynthetic: isSynthetic,
    lastRecordedStatus: lastRecordedStatus ?? null,
    lastRecordedAt: lastRecordedAt ?? null,

    // Raw EDMS state, passed through untouched
    edmsState: machine.state?.state || '',

    // Customer (flattened). Every field maps 1:1 to its EDMS property —
    // customerName is CustomerDto.name, customerDisplayName is the machine's
    // embedded displayName. No field falls back to another.
    customerId: machine.customer?.id || '',
    customerIfsId: machine.customer?.ifsId || '',
    customerName: machine.customer?.name || '',
    customerDisplayName: machine.customer?.displayName || '',
    customerDefaultLanguage: machine.customer?.defaultLanguage || '',
    customerCountry: machine.customer?.country || '',
    customerCountryCode: machine.customer?.countryCode || '',
    customerRepresentative: machine.customer?.representative || '',
    customerCategory: machine.customer?.customerCategory || '',

    // Plant (flattened)
    plant: machine.plant?.displayName || '',
    plantId: machine.plant?.id || '',

    // Address properties (flattened)
    address1: machine.address?.address1 || '',
    address2: machine.address?.address2 || '',
    city: machine.address?.city || '',
    state: machine.address?.state || '',
    zipCode: machine.address?.zipCode || '',
    country: machine.address?.country || '',
    countryCode: machine.address?.countryCode || '',
    county: machine.address?.county || '',
    latitude: machine.address?.latitude ?? null,
    longitude: machine.address?.longitude ?? null,

    // Edge device (flattened). EDMS returns no edge device at all for a machine
    // that was never provisioned, so the ED-guid decides the provisioning state.
    edgeDeviceId: machine.edgeDevice?.id || '',
    edgeDeviceGuid: machine.edgeDevice?.deviceId || '',
    edgeDeviceStatus: machine.edgeDevice?.deviceId
      ? 'Provisioned'
      : 'Not provisioned',

    // Device connectivity comes from EDMS device management, not from the
    // machine's own state. No device means there is nothing to be connected.
    edgeDeviceConnectionStatus: !machine.edgeDevice?.deviceId
      ? 'No device'
      : machine.edgeDevice.connectionStatus || 'Unknown',
    edgeDeviceName: machine.edgeDevice?.displayName || '',

    _original: machine,
  };
}
